import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'info' | 'warning' | 'error';
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  severity = 'warning',
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const getConfirmButtonColor = () => {
    switch (severity) {
      case 'error':
        return 'error' as const;
      case 'warning':
        return 'warning' as const;
      default:
        return 'primary' as const;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirm-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">
        <Typography variant="h6" component="div" color={severity === 'error' ? 'error' : 'inherit'}>
          {title}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText>
          {message}
        </DialogContentText>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          variant="outlined"
        >
          {cancelText || t('common.cancel')}
        </Button>
        <Button 
          onClick={onConfirm} 
          color={getConfirmButtonColor()}
          disabled={isLoading}
          variant="contained"
          autoFocus
        >
          {confirmText || t('common.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;

